import { Canvas } from '@react-three/fiber';
import { forwardRef } from 'react';
import type { CSSProperties } from 'react';
import type { ForwardedRef, ReactElement } from 'react';
import type { Graph3DData } from '../core/graph.types';
import type { Graph3DLayoutConfig } from '../core/layout.types';
import type {
  Graph3DEventHandlers,
  Graph3DEdgeRendererRegistry,
  Graph3DHandle,
  Graph3DInteractionConfig,
  Graph3DNodeRendererRegistry,
} from '../core/renderer.types';
import type { Graph3DRuntime } from '../core/runtime.types';
import type { Graph3DTheme } from '../core/theme.types';
import { Graph3DScene } from './Graph3DScene';

const INTERACTION_HINT_TEXT = '左键旋转 · 右键平移 · 滚轮缩放';

export interface Graph3DCanvasProps<TNodeData = unknown, TEdgeData = unknown> {
  data: Graph3DData<TNodeData, TEdgeData>;
  runtime?: Graph3DRuntime;
  layout?: Graph3DLayoutConfig;
  theme: Graph3DTheme;
  interactions?: Graph3DInteractionConfig;
  nodeRenderers?: Graph3DNodeRendererRegistry<TNodeData>;
  edgeRenderers?: Graph3DEdgeRendererRegistry<TEdgeData, TNodeData>;
  events?: Graph3DEventHandlers<TNodeData, TEdgeData>;
  showInteractionHint?: boolean;
  style?: CSSProperties;
  className?: string;
}

export const Graph3DCanvas = forwardRef(function Graph3DCanvasInner<
  TNodeData = unknown,
  TEdgeData = unknown,
>(
  {
    data,
    runtime,
    layout,
    theme,
    interactions,
    nodeRenderers,
    edgeRenderers,
    events,
    showInteractionHint = true,
    style,
    className,
  }: Graph3DCanvasProps<TNodeData, TEdgeData>,
  ref: ForwardedRef<Graph3DHandle>
) {
  return (
    <div
      className={className}
      style={{
        width: '100%',
        height: '100%',
        minHeight: 420,
        background: theme.background,
        position: 'relative',
        overflow: 'hidden',
        ...style,
      }}
    >
      <Canvas
        camera={{ position: [420, 240, 900], fov: 46, near: 0.1, far: 40000 }}
        dpr={[1, 1.6]}
      >
        <color attach="background" args={[theme.background]} />
        <Graph3DScene
          ref={ref}
          data={data}
          runtime={runtime}
          layout={layout}
          theme={theme}
          interactions={interactions}
          nodeRenderers={nodeRenderers}
          edgeRenderers={edgeRenderers}
          events={events}
        />
      </Canvas>
      {showInteractionHint && (
        <div
          aria-hidden="true"
          className="graph3d-canvas-interaction-hint"
          style={{
            position: 'absolute',
            right: 14,
            bottom: 14,
            zIndex: 2,
            maxWidth: 'calc(100% - 28px)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 10px',
            borderRadius: 999,
            border: '1px solid rgba(148,163,184,0.18)',
            background: 'rgba(255,255,255,0.68)',
            boxShadow: '0 6px 18px rgba(15,23,42,0.06)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            pointerEvents: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            fontFamily:
              'system-ui, -apple-system, PingFang SC, Microsoft YaHei, sans-serif',
            fontSize: 11,
            lineHeight: '15px',
            color: 'rgba(51,65,85,0.88)',
            whiteSpace: 'nowrap',
          }}
        >
          {INTERACTION_HINT_TEXT}
        </div>
      )}
    </div>
  );
}) as <TNodeData = unknown, TEdgeData = unknown>(
  props: Graph3DCanvasProps<TNodeData, TEdgeData> & {
    ref?: ForwardedRef<Graph3DHandle>;
  }
) => ReactElement;
